import { useEffect, useState, useMemo } from "react";
import { Sparkles, Plus, Trash2, MapPin, FileText, Calendar, Euro, X, Clock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";

const CATEGORY_LABELS = {
  deep_clean: "Limpeza profunda",
  post_obra: "Pós-obra",
  mudanca: "Mudança",
  ar_condicionado: "Ar condicionado",
  estofos: "Estofos / sofás",
  vidros: "Vidros",
  outro: "Outro",
};

export default function AdminManualCleanings() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const [draft, setDraft] = useState({
    date: todayLocalIso(),
    category: "deep_clean",
    description: "",
    amount: "",
    location: "",
    notes: "",
    paid: true,
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manual-cleanings");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!draft.description.trim() || !draft.amount || !draft.date) {
      toast.error("Preencha data, descrição e valor.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/manual-cleanings", {
        date: draft.date,
        category: draft.category,
        description: draft.description,
        amount: parseFloat(draft.amount),
        location: draft.location,
        notes: draft.notes,
        paid: draft.paid,
      });
      toast.success(draft.paid ? "Limpeza registada como paga." : "Limpeza registada (por receber).");
      setDraft({
        date: todayLocalIso(), category: "deep_clean",
        description: "", amount: "", location: "", notes: "",
        paid: true,
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function del(id) {
    if (!window.confirm("Eliminar este registo? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/admin/manual-cleanings/${id}`);
      toast.success("Registo eliminado.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function togglePaid(it) {
    try {
      await api.patch(`/admin/manual-cleanings/${it.id}/paid?paid=${!it.paid}`);
      toast.success(!it.paid ? "Marcada como paga · entra no saldo." : "Marcada como por receber.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const last30 = useMemo(() => {
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    return items.filter(it => (it.date || "") >= cutoff && it.paid !== false)
                .reduce((acc, it) => acc + (it.amount || 0), 0);
  }, [items]);

  const pendingTotal = useMemo(() =>
    items.filter(it => it.paid === false).reduce((a, it) => a + (it.amount || 0), 0),
  [items]);

  return (
    <div className="space-y-8" data-testid="admin-manual-cleanings">
      <div>
        <div className="eyebrow">Contabilidade · extras</div>
        <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
          <Sparkles size={30} className="text-[#d4af37]" /> Limpezas extra
        </h1>
        <p className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Registe aqui limpezas fora do padrão — deep clean, pós-obra,
          mudanças, etc. Sem necessidade de criar reserva nem cliente.
          O valor entra automaticamente no painel financeiro.
        </p>
      </div>

      {/* Totais */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Recebido (acumulado)" value={`${total.toFixed(2)}€`} testId="mc-total" highlight />
        <Stat label="Últimos 30 dias · pagas" value={`${last30.toFixed(2)}€`} testId="mc-30d" />
        <Stat label="Por receber" value={`${pendingTotal.toFixed(2)}€`} testId="mc-pending" />
        <Stat label="Nº de registos" value={items.length} testId="mc-count" />
      </div>

      {/* Form */}
      <form onSubmit={submit} className="lux-card p-5 space-y-4" data-testid="mc-form">
        <div className="eyebrow text-[#d4af37]">Adicionar limpeza extra</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Data" type="date" value={draft.date}
                 onChange={v => setDraft(d => ({ ...d, date: v }))} testId="mc-date" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Categoria</span>
            <select
              value={draft.category}
              onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
              className="mt-1 w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none px-3 py-2 text-sm text-[#fdfbf7] rounded-sm"
              data-testid="mc-category"
            >
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
        </div>
        <Field label="Descrição" value={draft.description}
               placeholder="Ex: Limpeza profunda T3 — Ponta do Sol"
               onChange={v => setDraft(d => ({ ...d, description: v }))} testId="mc-description" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Valor (€)" type="number" step="0.01" value={draft.amount}
                 placeholder="0.00"
                 onChange={v => setDraft(d => ({ ...d, amount: v }))} testId="mc-amount" />
          <Field label="Local (opcional)" value={draft.location}
                 placeholder="Funchal · Caminho dos Tornos 12"
                 onChange={v => setDraft(d => ({ ...d, location: v }))} testId="mc-location" />
        </div>
        <Field label="Notas (opcional)" value={draft.notes}
               onChange={v => setDraft(d => ({ ...d, notes: v }))} testId="mc-notes" />
        <label className="flex items-start gap-3 p-3 border border-[#1a4640] rounded-sm cursor-pointer hover:border-[#d4af37]/40 transition-colors">
          <input
            type="checkbox"
            checked={draft.paid}
            onChange={e => setDraft(d => ({ ...d, paid: e.target.checked }))}
            className="mt-0.5 w-4 h-4 accent-[#d4af37]"
            data-testid="mc-paid-checkbox"
          />
          <div className="text-xs">
            <div className="text-[#fdfbf7] font-medium">Já recebi o valor</div>
            <div className="text-[10px] text-[#a0b2af] mt-0.5">
              Marque desmarcada se a limpeza ainda está por pagar. Só limpezas pagas contam para o saldo financeiro;
              as não pagas vão para «Pagamentos por vir».
            </div>
          </div>
        </label>
        <button type="submit" disabled={saving}
                className="btn-gold px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="mc-submit">
          <Plus size={14} /> {saving ? "A guardar…" : "Registar limpeza"}
        </button>
      </form>

      {/* List */}
      <section className="lux-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[#1a4640] flex items-center justify-between">
          <h2 className="font-display text-xl">Histórico ({items.length})</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#a0b2af]">A carregar…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Sparkles size={32} className="mx-auto text-[#d4af37]/40 mb-2" />
            <p className="text-sm text-[#a0b2af]">
              Ainda sem registos. Adicione a primeira limpeza extra acima.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#1a4640]">
            {items.map(it => (
              <article key={it.id}
                       onClick={() => setOpenItem(it)}
                       className="p-5 flex items-start justify-between gap-4 hover:bg-[#062c27]/70 transition-colors cursor-pointer"
                       data-testid={`mc-row-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-[#d4af37] px-2 py-0.5 border border-[#d4af37]/40 rounded-sm">
                      {CATEGORY_LABELS[it.category] || it.category}
                    </span>
                    {it.paid === false ? (
                      <span className="text-[10px] uppercase tracking-widest text-[#fcd34d] px-2 py-0.5 border border-[#fcd34d]/40 rounded-sm bg-[#fcd34d]/5"
                            data-testid={`mc-badge-pending-${it.id}`}>
                        Por receber
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-[#10b981] px-2 py-0.5 border border-[#10b981]/40 rounded-sm bg-[#10b981]/5"
                            data-testid={`mc-badge-paid-${it.id}`}>
                        Pago
                      </span>
                    )}
                    <span className="text-[11px] text-[#a0b2af] inline-flex items-center gap-1">
                      <Calendar size={11} /> {it.date}
                    </span>
                    {it.location && (
                      <span className="text-[11px] text-[#a0b2af] inline-flex items-center gap-1">
                        <MapPin size={11} /> {it.location}
                      </span>
                    )}
                  </div>
                  <p className="text-[#fdfbf7] text-sm mt-2 break-words">{it.description}</p>
                  {it.notes && (
                    <p className="text-[11px] text-[#a0b2af] mt-1 italic break-words inline-flex items-start gap-1">
                      <FileText size={11} className="mt-0.5 shrink-0" /> {it.notes}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0" onClick={(e) => e.stopPropagation()}>
                  <div className={`font-display text-2xl ${it.paid === false ? "text-[#fcd34d]" : "text-[#d4af37]"}`}
                       data-testid={`mc-amount-${it.id}`}>
                    {(it.amount || 0).toFixed(2)}€
                  </div>
                  <button onClick={() => togglePaid(it)}
                          className="mt-2 block ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                          data-testid={`mc-toggle-paid-${it.id}`}>
                    {it.paid === false ? "Marcar como pago" : "Marcar como por receber"}
                  </button>
                  <button onClick={() => del(it.id)}
                          className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#fca5a5] hover:text-[#ef4444]"
                          data-testid={`mc-delete-${it.id}`}>
                    <Trash2 size={11} /> Eliminar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {openItem && (
        <DetailModal item={openItem} onClose={() => setOpenItem(null)}
                     onDelete={async () => { await del(openItem.id); setOpenItem(null); }} />
      )}
    </div>
  );
}

function DetailModal({ item, onClose, onDelete }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="mc-detail-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#062c27] border border-[#1a4640] max-w-xl w-full p-6 space-y-5 my-8"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-[#d4af37]">{CATEGORY_LABELS[item.category] || item.category}</div>
            <h3 className="font-display text-2xl text-[#fdfbf7] mt-1 break-words">
              {item.description}
            </h3>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="text-center py-3 border-y border-[#1a4640]">
          <div className="eyebrow">Receita registada</div>
          <div className="font-display text-4xl text-[#d4af37] mt-1">
            {(item.amount || 0).toFixed(2)}€
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <DetailRow icon={Calendar} label="Data" value={item.date} />
          {item.location && <DetailRow icon={MapPin} label="Local" value={item.location} />}
          {item.notes && <DetailRow icon={FileText} label="Notas" value={item.notes} multi />}
          {item.created_at && (
            <DetailRow
              icon={Clock}
              label="Registado"
              value={new Date(item.created_at).toLocaleString("pt-PT")}
            />
          )}
          {item.created_by && (
            <DetailRow icon={UserIcon} label="Por (admin)" value={<span className="font-mono text-xs">{item.created_by}</span>} />
          )}
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-[#1a4640]">
          <button onClick={onDelete}
                  className="text-[11px] uppercase tracking-widest text-[#fca5a5] hover:text-[#ef4444] inline-flex items-center gap-1.5"
                  data-testid="mc-detail-delete">
            <Trash2 size={12} /> Eliminar registo
          </button>
          <button onClick={onClose}
                  className="px-5 py-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-white">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, multi = false }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={13} className="text-[#a0b2af] mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
        <div className={`text-[#fdfbf7] ${multi ? "whitespace-pre-wrap" : ""} break-words`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testId, highlight }) {
  return (
    <div className={`lux-card p-4 ${highlight ? "border-[#d4af37]/40" : ""}`} data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className={`mt-2 font-display text-3xl ${highlight ? "text-[#d4af37]" : "text-[#fdfbf7]"}`}>
        {value}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</span>
      <input
        type={type}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none px-3 py-2 text-sm text-[#fdfbf7] rounded-sm placeholder:text-[#1a4640]"
        data-testid={testId}
      />
    </label>
  );
}
